import * as React from 'react';

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ISignInItem,
    ISignInLoading,
    ISignInLocalAccount,
    ISignInViewProps,
    ISignInSocialAccount,
    ISignInSocialItem
} from '@msdyn365-commerce-modules/account-management/src/modules/sign-in/./sign-in';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ISignInProps } from '../definition-extensions/sign-in.ext.props.autogenerated';
import SignInTextComponent from './components/sign-in-text';
import classnames from 'classnames';

const moduleClassName: string = 'ms-sign-in';
const LocalAccount: React.FC<ISignInLocalAccount> = ({ localAccount, items, forgetPassword, disclaimer, error, signInButton }) => {
    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignInItem) => {
                    return (
                        <Node key={item.key} {...item.wrapper}>
                            {item.label}
                            {item.key === 'logonIdentifier' ? _renderInput(item) : item.input}
                            {item.error}
                        </Node>
                    );
                })}
            {disclaimer}
            {error}
            {signInButton}
            {forgetPassword}
        </Node>
    );
};

const _renderInput = (item: ISignInItem): JSX.Element | undefined | null => {
    return (
        item && (
            <input
                id='email'
                type='email'
                pattern='^[a-zA-Z0-9!#$%&amp;&#39;+^_`{}~-]+(?:.[a-zA-Z0-9!#$%&amp;&#39;+^_`{}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$'
                className='ms-sign-in__account-item-input ms-sign-in__account-item-logonIdentifier'
                aria-label='Please enter the email address'
                name='email'
            />
        )
    );
};

const SignInLoading: React.FC<ISignInLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SocialAccount: React.FC<ISignInSocialAccount> = ({ socialAccounts, items }) => {
    return (
        <Node {...socialAccounts}>
            {items &&
                items
                    .filter(item => item.key !== 'MicrosoftAccountExchange')
                    .map((item: ISignInSocialItem) => {
                        return (
                            <Node key={item.key} {...item.button}>
                                {item.image}
                                {item.text}
                            </Node>
                        );
                    })}
        </Node>
    );
};

const SignInView: React.FC<ISignInViewProps & ISignInProps<{}>> = props => {
    const {
        signIn,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        signInSection,
        signUpSection,
        signInSectionHeading,
        signInLocalAccount,
        signUpSectionHeading,
        signUpLink,
        config: { siteMigrationInfo }
    } = props;
    //  VSI Customization rendering button from View file - Google Signin - Start
    function _renderSocialAccount(id: string, text: string, ariaLabel: string, iconImage?: MsDyn365.IImageData): ISignInSocialItem {
        return {
            key: id,
            button: {
                id,
                tag: 'button',
                className: classnames(
                    `${moduleClassName}__social-account`,
                    `${moduleClassName}__social-account-${id}`,
                    'accountButton',
                    'msc-btn'
                ),
                'aria-label': ariaLabel
            },
            image: _createImageMarkup(iconImage),
            text: <SignInTextComponent className={`${moduleClassName}__social-account-text`} text={text} />
        };
    }

    function _createImageMarkup(iconImage?: MsDyn365.IImageData): React.ReactNode | null {
        if (iconImage) {
            const imageProps = {
                gridSettings: props.context.request.gridSettings || {},
                imageSettings: iconImage && iconImage.imageSettings
            };
            const pictureClassName = `${moduleClassName}__social-account-picture`;
            return (
                <div className={pictureClassName}>
                    <MsDyn365.Image
                        className={`${pictureClassName}__image`}
                        {...iconImage}
                        {...imageProps}
                        requestContext={props.context.request}
                        editProps={{ key: iconImage || {}, requestContext: props.context.request }}
                        shouldSkipToMainImage
                    />
                </div>
            );
        }
        return null;
    }
    const signInSocialAccount = {
        socialAccounts: {
            className: `${moduleClassName}__social-accounts`
        },
        items: [
            _renderSocialAccount(
                'FacebookExchange',
                props.resources.facebookButtonText,
                props.resources.facebookButtonAriaLabel,
                props.config.facebookIcon
            ),
            _renderSocialAccount(
                'MicrosoftAccountExchange',
                props.resources.microsoftButtonText,
                props.resources.microsoftButtonAriaLabel,
                props.config.microsoftIcon
            ),
            _renderSocialAccount(
                'GoogleExchange',
                props.resources.googleButtonText,
                props.resources.googleButtonAriaLabel,
                props.config.googleIcon
            )
        ]
    };

    //  VSI Customization rendering button from View file - Google Signin - End
    return (
        <Module {...signIn}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignInLoading {...loading} />}
            <Node {...aadConainer}>
                <Node {...signInSection}>
                    {signInSectionHeading}
                    {/* Render site migration info */}
                    <Node className='migration-info'>
                        {siteMigrationInfo && (
                            <MsDyn365.RichTextComponent
                                text={siteMigrationInfo}
                                editProps={{ onEdit: handleTextChange(props), requestContext: props.context.request }}
                            />
                        )}
                    </Node>
                    {LocalAccount(signInLocalAccount)}
                    {SocialAccount(signInSocialAccount)}
                </Node>
                <Node {...signUpSection}>
                    {signUpSectionHeading}
                    {signUpLink}
                </Node>
            </Node>
        </Module>
    );
};
const handleTextChange = (props: ISignInViewProps & ISignInProps<{}>) => (event: MsDyn365.ContentEditableEvent) =>
    (props.config.siteMigrationInfo = event.target.value);
export default SignInView;
